// Origin-local, tab-persistent recovery budget. Never store tokens, callback
// parameters or provider error descriptions here. Only API success or an
// explicit Login click resets it; an OIDC callback alone proves no API access.
const KEY = 'deploy_console_auth_recovery';
const RENEWAL_KEY = 'deploy_console_auth_renewal_pending';
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
export const AUTH_RECOVERY_ERROR = 'Sign-in could not establish API access. The server may have an audience, issuer or identity configuration mismatch. Contact the administrator, then choose Retry sign-in. No automatic redirect will be repeated.';

export function authRecovery(storage: Storage) {
  const read = () => { try { return storage.getItem(KEY); } catch { return 'blocked'; } };
  const block = () => { try { storage.setItem(KEY, 'blocked'); } catch { /* fail closed in caller */ } };
  return {
    blocked: () => read() === 'blocked',
    block,
    reset: () => { try { storage.removeItem(KEY); storage.removeItem(RENEWAL_KEY); } catch { /* automatic login remains denied */ } },
    // Token replacement and a fresh /api/me effect must not create a fresh
    // renewal budget. Only verified API access or explicit sign-in resets it.
    claimSilentRenew: () => {
      try {
        if (read() === 'blocked' || storage.getItem(RENEWAL_KEY) !== null) return false;
        storage.setItem(RENEWAL_KEY, 'pending');
        return true;
      } catch { return false; }
    },
    claimAutomaticLogin: () => {
      if (read() !== null) { block(); return false; }
      try { storage.setItem(KEY, 'recovering'); return true; } catch { return false; }
    }
  };
}

export function needsInteractiveSession(error: unknown): boolean {
  const code = error && typeof error === 'object' && 'error' in error ? error.error : undefined;
  // Configuration, transport and unknown errors are not evidence that another
  // OIDC round trip will help. Keep them visible for explicit user retry.
  return code === 'login_required' || code === 'interaction_required' || code === 'invalid_grant';
}

export function isTerminalApiAuthCode(code?: string): boolean {
  return ['invalid_audience', 'invalid_issuer', 'invalid_client', 'auth_configuration_error']
    .includes((code ?? '').toLowerCase());
}

// The retry's token replacement advances the profile generation once. Keep
// its terminal 401 bound to that one replay, including when the token string
// is reused, while rejecting replies from any other session generation.
export function mayRecoverUnauthorized(
  request: { authGeneration: number; profileGeneration: number; token: string },
  current: { authGeneration: number; profileGeneration: number; token: string },
  retryToken?: string
): boolean {
  if (request.authGeneration !== current.authGeneration) return false;
  if (retryToken !== undefined) {
    return current.profileGeneration === request.profileGeneration + 1 && current.token === retryToken;
  }
  return request.profileGeneration === current.profileGeneration && current.token === request.token;
}
